import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Wrench, Camera, Plus, Package, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { KpiCard } from "./components/KpiCard";

/**
 * MechanicDashboard — bay-focused, task-driven.
 * Do NOT show business-level charts.
 */
export default function MechanicDashboard({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/tasks/my").then((r) => setTasks(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = tasks.find((t) => t.status === "in_progress");
  const upNext = tasks.filter((t) => t.status !== "in_progress" && t.status !== "done");
  const paused = tasks.filter((t) => t.status === "paused");
  const doneToday = tasks.filter((t) => {
    const day = new Date().toISOString().slice(0, 10);
    return t.status === "done" && (t.updated_at || "").startsWith(day);
  }).length;
  const today = new Date().toISOString().slice(0, 10);
  const dueToday = upNext.filter((t) => (t.due_at || "").startsWith(today)).length;

  const hi = (user?.name || "").split(" ")[0] || "Mechanic";

  return (
    <div className="space-y-6" data-testid="mechanic-dashboard-v3">
      <header className="flex flex-col gap-1">
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946]">My Bay</div>
        <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d3557]">Hello {hi}</h1>
        <p className="text-[13px] text-[#667085]">Focus on today&apos;s work. Big buttons, no distractions.</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testId="mec-kpi-active" label="My Active Jobs" value={active ? 1 : 0} hint={active ? active.task_name : "Idle"} Icon={Wrench} tone="red" />
        <KpiCard testId="mec-kpi-due" label="Tasks Due Today" value={dueToday} hint="Complete before EOD" Icon={Wrench} tone="orange" />
        <KpiCard testId="mec-kpi-parts" label="Waiting for Parts" value={paused.length} hint="Paused until parts arrive" Icon={Package} tone="steel" />
        <KpiCard testId="mec-kpi-done" label="Completed Today" value={doneToday} hint="Nice progress" Icon={CheckCircle2} tone="green" />
      </div>

      {/* Big touch actions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <BigAction label="Start Task" tone="primary" Icon={Wrench} onClick={() => nav("/my-jobs")} testId="mec-action-start" />
        <BigAction label="Add Finding" tone="steel" Icon={Plus} onClick={() => nav("/my-jobs")} testId="mec-action-finding" />
        <BigAction label="Request Part" tone="orange" Icon={Package} onClick={() => nav("/inventory")} testId="mec-action-part" />
        <BigAction label="Upload Photo" tone="deep" Icon={Camera} onClick={() => nav("/my-jobs")} testId="mec-action-photo" />
        <BigAction label="Mark Complete" tone="green" Icon={CheckCircle2} onClick={() => nav("/my-jobs")} testId="mec-action-complete" />
      </div>

      {/* Working on now */}
      <section className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="mec-active-card">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#e63946] mb-1">Working on now</div>
        {active ? (
          <Link to="/my-jobs" className="block">
            <div className="text-[22px] font-bold text-[#1d3557]">{active.task_name}</div>
            <div className="text-[12.5px] text-[#667085] mt-1">
              {active.job?.job_number || "—"} · {active.job?.vehicle?.reg_number || "—"}
            </div>
          </Link>
        ) : (
          <div className="text-[13px] text-[#667085]">
            No task in progress. Pick one from your queue below.
          </div>
        )}
      </section>

      {/* Up Next */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="mec-up-next">
        <header className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-[#1d3557]">Up Next</h3>
          <Link to="/my-jobs" className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946]">
            Open queue →
          </Link>
        </header>
        {upNext.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Nothing queued for you.</div>
        ) : (
          <ul className="divide-y divide-[#F1FAEE]">
            {upNext.slice(0, 8).map((t) => (
              <li key={t.id} className="px-5 py-3 flex items-center gap-3 hover:bg-[#F7F9FC]" data-testid={`mec-next-${t.id}`}>
                <div className="w-8 h-8 rounded-md bg-[#F1FAEE] grid place-items-center text-[#457b9d] shrink-0">
                  <Wrench size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1d3557] truncate">{t.task_name}</div>
                  <div className="text-[11px] text-[#667085] truncate">
                    {t.job?.job_number || "—"} · {t.job?.vehicle?.reg_number || "—"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function BigAction({ label, Icon, onClick, tone = "primary", testId }) {
  const toneCls = {
    primary: "bg-[#e63946] hover:bg-[#d02f3c] text-white",
    steel: "bg-[#457b9d] hover:bg-[#3a6b8a] text-white",
    deep: "bg-[#1d3557] hover:bg-[#152941] text-white",
    orange: "bg-[#F79009] hover:bg-[#dc7d05] text-white",
    green: "bg-[#12B76A] hover:bg-[#0f9a58] text-white",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-20 rounded-xl grid place-items-center transition-colors ${toneCls}`}
    >
      <div className="text-center">
        <Icon size={22} className="mx-auto mb-1" />
        <div className="text-[13px] font-semibold">{label}</div>
      </div>
    </button>
  );
}
